/**
 * Overview tab of service plans screen - displaying the list of service plan account has set up
 */
import qs from 'qs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import DropdownField from '@payaca/components/dropdownField/DropdownField';
import {
  InputColourVariant,
  InputStyleVariant,
} from '@payaca/components/inputWrapper/InputWrapper';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import PaginationControl from '@payaca/components/paginationControl/PaginationControl';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import CustomerFilterControl from '../customerFilterControl/CustomerFilterControl';
import ServicePlanPeriodCard from './ServicePlanPeriodCard';

import * as servicePlansActions from '@payaca/store/servicePlans/servicePlansActions';

import { useSelector } from '../../../api/state';

import { GetServicePeriodsRequestData } from '@payaca/store/servicePlans/servicePlansTypes';
import { ServicePlanPeriod } from '@payaca/types/service-plans';

import './ServicePlansServicePeriods.sass';

const PAGE_SIZE = 20;

type ServicePeriodsFilters = Omit<
  GetServicePeriodsRequestData,
  'offset' | 'limit'
>;

const servicePeriodStatusOptions: {
  value: ServicePlanPeriod['status'];
  label: string;
}[] = [
  { value: 'unarranged', label: 'Unarranged' },
  { value: 'arranged', label: 'Arranged' },
  { value: 'fulfilled', label: 'Complete' },
];

const ServicePlansServicePeriods = () => {
  const dispatch = useDispatch();
  // servicePlanPublicIds in query to filter by plan
  const servicePlanPublicIds = useMemo(() => {
    const query = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    const planIds = query?.servicePlanPublicIds as string;
    return planIds ? planIds.split(',') : [];
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: location.
  }, [location]);
  const customerId = useMemo(() => {
    const query = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    if (query?.customerId) {
      return Number(query?.customerId);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: location.
  }, [location]);

  const [filters, setFilters] = useState<ServicePeriodsFilters>({
    servicePlanPublicIds,
    statuses: ['unarranged', 'arranged'],
    when: [],
    customerId: customerId,
  });
  const [pageNumber, setPageNumber] = useState(1);

  // Any filter change resets back to the first page so we don't land on a page
  // that no longer exists for the new result set.
  const updateFilters = (update: Partial<ServicePeriodsFilters>) => {
    setFilters((previous) => ({ ...previous, ...update }));
    setPageNumber(1);
  };

  const listedServicePlans = useSelector(
    (state) => state.servicePlans.listedServicePlans
  );

  const listedServicePlanPeriodsPage = useSelector(
    (state) => state.servicePlans.listedServicePlanPeriodsPage
  );

  useEffect(() => {
    dispatch(
      servicePlansActions.getServicePeriods.request({
        ...filters,
        offset: (pageNumber - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      })
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [filters, pageNumber]);

  const isGettingServicePeriods = useSelector(
    (state) => state.servicePlans.isGettingServicePeriods
  );

  const servicePlanOptions = useMemo(() => {
    return (
      listedServicePlans?.map((listedServicePlan) => ({
        value: listedServicePlan.publicId,
        label: listedServicePlan.name,
      })) || []
    );
  }, [listedServicePlans]);

  const hasFiltersApplied = useMemo(() => {
    // An empty or fully-selected status list means "all statuses" (the request
    // omits it), so only a partial selection counts as a filter.
    const statusesFilterApplied =
      filters.statuses.length > 0 &&
      filters.statuses.length < servicePeriodStatusOptions.length;

    return Boolean(
      filters.servicePlanPublicIds?.length ||
      filters.customerId ||
      filters.when?.length ||
      statusesFilterApplied
    );
  }, [filters]);

  const servicePlanPeriods = listedServicePlanPeriodsPage?.items;

  return (
    <div className="service-plans-service-periods">
      {/* Filter */}
      <div className="service-periods-filters">
        <div className="filter-container">
          <DropdownField
            label="Plan"
            value={filters?.servicePlanPublicIds || []}
            name="servicePlanPublicIds"
            options={servicePlanOptions}
            onChange={(value) => {
              updateFilters(value);
            }}
            emptyText="All Plans"
            multiple={true}
            additionalSelectProps={{
              displayEmpty: true,
            }}
            styleVariant={InputStyleVariant.STANDARD}
            colourVariant={InputColourVariant.WHITE}
            hasBoxShadow={false}
            displayCheckboxes={true}
          />
        </div>
        <div className="filter-container">
          <CustomerFilterControl
            selectedCustomerId={filters.customerId || undefined}
            onChange={(customerId) => {
              updateFilters({ customerId });
            }}
            displayValue={filters.customerId ? undefined : 'All Customers'}
          />
        </div>
        <div className="filter-container">
          <DropdownField
            label="Status"
            value={filters?.statuses || []}
            name="statuses"
            options={servicePeriodStatusOptions}
            onChange={(value) => {
              updateFilters(value);
            }}
            emptyText="All statuses"
            multiple={true}
            additionalSelectProps={{
              displayEmpty: true,
            }}
            styleVariant={InputStyleVariant.STANDARD}
            colourVariant={InputColourVariant.WHITE}
            hasBoxShadow={false}
            displayCheckboxes={true}
          />
        </div>
        <div className="filter-container">
          <DropdownField
            label="When"
            value={filters?.when || []}
            name="when"
            options={[
              { label: 'Past', value: 'past' },
              { label: 'Upcoming', value: 'upcoming' },
              { label: 'Current', value: 'current' },
            ]}
            onChange={(value) => {
              updateFilters(value);
            }}
            emptyText="Any time"
            multiple={true}
            additionalSelectProps={{
              displayEmpty: true,
            }}
            styleVariant={InputStyleVariant.STANDARD}
            colourVariant={InputColourVariant.WHITE}
            hasBoxShadow={false}
            displayCheckboxes={true}
          />
        </div>
      </div>
      {isGettingServicePeriods ? (
        <div className="loader-container">
          <MiniLoader />
        </div>
      ) : (
        <>
          <ResponsiveViewWrapper className="service-periods-container">
            {servicePlanPeriods?.length ? (
              servicePlanPeriods.map((servicePlanPeriod) => (
                <ServicePlanPeriodCard
                  servicePlanPeriod={servicePlanPeriod}
                  key={servicePlanPeriod.publicId}
                />
              ))
            ) : hasFiltersApplied ? (
              <span className="no-results">No results found</span>
            ) : (
              <></>
            )}
          </ResponsiveViewWrapper>
          {/* Render whenever there are matches, even if the current page is
              empty (e.g. records changed since the page loaded), so the user
              can always navigate back to a populated page. */}
          {!!listedServicePlanPeriodsPage?.totalItemCount && (
            <PaginationControl
              pageNumber={listedServicePlanPeriodsPage.pageNumber}
              pageSize={listedServicePlanPeriodsPage.pageSize}
              totalItemCount={listedServicePlanPeriodsPage.totalItemCount}
              onSelectPage={setPageNumber}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ServicePlansServicePeriods;
